import KPICards from '../Components/KPICards';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Components/Table';
import { ArrowRight } from 'lucide-react';

import {
  kpiPaymentData,
  paymentFlowData,
  failureReasonsData,
  failedPaymentsData,
} from '../Data/mockData';

export default function Payment() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* KPI Cards */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Payment Performance Metrics
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {kpiPaymentData.map((kpi, index) => (
            <KPICards
              key={index}
              label={kpi.label}
              value={kpi.value}
              trend={kpi.trend}
              bgColor={kpi.bgColor}
            />
          ))}
        </div>
      </div>

      {/* Payment Flow */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 24 }}>
          Payment Flow Funnel
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Requested */}
          <div style={{ flex: 1 }}>
            <div style={{ backgroundColor: '#bfdbfe', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Requested</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1d4ed8' }}>{paymentFlowData.requested.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>100%</div>
            </div>
          </div>

          <ArrowRight style={{ width: 32, height: 32, color: '#9ca3af', margin: '0 12px' }} />

          {/* Processed */}
          <div style={{ flex: 1 }}>
            <div style={{ backgroundColor: '#e9d5ff', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Processed</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed' }}>{paymentFlowData.processed.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>97.9%</div>
            </div>
          </div>

          <ArrowRight style={{ width: 32, height: 32, color: '#9ca3af', margin: '0 12px' }} />

          {/* Settled */}
          <div style={{ flex: 1 }}>
            <div style={{ backgroundColor: '#bbf7d0', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Settled</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#047857' }}>{paymentFlowData.settled.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>96.8%</div>
            </div>
          </div>

          {/* Failed */}
          <div style={{ flex: 1, marginLeft: 12 }}>
            <div style={{ backgroundColor: '#fee2e2', borderRadius: 8, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Failed</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#b91c1c' }}>{paymentFlowData.failed.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>3.2%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Failure Analysis */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
          Failure Reason Analysis
        </h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={failureReasonsData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis dataKey="reason" type="category" tick={{ fontSize: 12 }} width={150} />
            <Tooltip />
            <Bar dataKey="count" fill="#ef4444" name="Failed Payments" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Failed Payments Table */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
        <div style={{ padding: 24, borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>Failed Payment Details</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment ID</TableHead>
                <TableHead>Corporate</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Failure Reason</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {failedPaymentsData.map((row, index) => {
                const statusStyles =
                  row.status === 'Failed'
                    ? { backgroundColor: '#fee2e2', color: '#b91c1c' }
                    : row.status === 'Retrying'
                    ? { backgroundColor: '#fef3c7', color: '#b45309' }
                    : { backgroundColor: '#dbeafe', color: '#1e40af' };

                return (
                  <TableRow key={index}>
                    <TableCell style={{ fontWeight: 500 }}>{row.id}</TableCell>
                    <TableCell>{row.corporate}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.reason}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          display: 'inline-flex',
                          padding: '4px 8px',
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 9999,
                          ...statusStyles,
                        }}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
